import { createContext, useContext, useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

const ThemeCtx = createContext({ theme: "light", setTheme: () => {} });

const STORAGE_KEY = "tr_theme";

function applyTheme(theme) {
    const root = document.documentElement;
    if (theme === "dark") {
        root.classList.add("dark");
        root.style.colorScheme = "dark";
    } else {
        root.classList.remove("dark");
        root.style.colorScheme = "light";
    }
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => {
        if (typeof window === "undefined") return "light";
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (saved === "dark" || saved === "light") return saved;
        return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    });

    useEffect(() => { applyTheme(theme); }, [theme]);

    const setTheme = (t) => {
        window.localStorage.setItem(STORAGE_KEY, t);
        setThemeState(t);
    };

    return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
}

export function useTheme() { return useContext(ThemeCtx); }

export function ThemeToggle({ className = "" }) {
    const { theme, setTheme } = useTheme();
    const isDark = theme === "dark";
    return (
        <button
            onClick={() => setTheme(isDark ? "light" : "dark")}
            data-testid="theme-toggle"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className={`relative w-9 h-9 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-amber-300 hover:border-slate-400 dark:hover:border-slate-500 transition-colors ${className}`}
        >
            <Sun size={15} className={`absolute transition-all ${isDark ? "opacity-0 rotate-90 scale-50" : "opacity-100 rotate-0 scale-100"}`} />
            <Moon size={15} className={`absolute transition-all ${isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-50"}`} />
        </button>
    );
}
